/**
 * The one editor box — quote builder and package builder alike.
 *
 * `design-remediation.md` §0.5 moved the *rows* into `builder/rows.tsx`. This
 * is the other half: the box that opens when you click one of them. A visit,
 * a service and a line are three different things, but the box that edits
 * them answers the same three questions in the same three places, so it is
 * one component and not three near-copies:
 *
 *   **header** — a tile, the name in 15/700, and one line of facts under it
 *                saying what this thing is. Never a bare word like "Visit".
 *   **body**   — `FieldShell` fields, 11.5px sentence-case labels (§2), in a
 *                12px column. One grid row per question, never one per field.
 *   **footer** — the fact this box is about on the left ("Line total $2,671",
 *                "3 services · 12 visits"), the actions on the right, Remove
 *                in danger before Done in primary.
 *
 * Everything here used to live in `quote/quote-editors.tsx` behind a
 * page-local `Field` drawing 11px uppercase labels — the fourth of the five
 * process failures §0 names. The labels are sentence case now and the
 * components are somewhere the package builder can reach.
 */
import {
  useCallback, useLayoutEffect, useRef, useState, type ComponentType, type ReactNode,
} from 'react'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { Button } from '../components/primitives.js'
import { createPortal } from 'react-dom'
import { Popover, useDismiss } from '../components/overlays.js'
import { controlClass } from '../components/form/field.js'
import { MenuSelect, type MenuOption } from '../components/menu-select.js'
import { KindTile, type TileKind } from './kind-tile.js'
import { CANCEL, DONE, REMOVE } from './copy.js'
import { IconChevronDown, IconClose } from '../frame/icons.js'

/**
 * A popover hanging off what was clicked, or a sheet up from the bottom.
 *
 * One component rather than two, because the *contents* of every editor here
 * are identical on both — §7's folding rule. What changes is where the box
 * comes from, and a phone has no room for a box that hangs off a 14px chip.
 */
export function EditorShell({
  open, onClose, anchor, label, width = 480, maxHeight, header, footer, fullScreen, fill,
  tone, nub, layer, children,
}: {
  open: boolean
  onClose: () => void
  anchor: HTMLElement | null
  label: string
  /**
   * What the header says, when the name alone is not enough.
   *
   * The line editor's is a tile, the name and "Material · Hardscape · $3.10 /
   * sq ft cost" — three facts that decide whether this is the line somebody
   * meant to open. Left out, the header is the label in bold, which is right
   * for the five-field editors.
   */
  header?: ReactNode
  width?: number
  /** A ceiling of its own, under whatever the viewport allows. */
  maxHeight?: number
  footer?: ReactNode
  /**
   * The contents manage their own scrolling, so the shell must not add a
   * second one.
   *
   * A form scrolls as a block — that is this shell's default and it is right
   * for five fields and a Done. A search does not: its box and its chips stay
   * put and only the list under them moves, which means the list is the
   * scroller and the body around it must be a plain flex column. Left to
   * scroll as well, the two nested and the picker had a scrollbar inside a
   * scrollbar.
   */
  fill?: boolean
  /**
   * Full height on a phone rather than a sheet — §7's Add screen.
   *
   * A search is not a form: the answer is a list, the list is long, and a
   * sheet that covers two thirds of the screen shows four results and then
   * fights the keyboard for the rest. Every other editor is a sheet, because
   * every other editor is five fields and a Done.
   */
  fullScreen?: boolean
  /**
   * `internal` is qb-04's margin popover: the whole box on the tinted
   * surface, no rule under the header (the scope control sits straight under
   * it) and a 12px footer. Internal numbers are tinted, never white.
   */
  tone?: 'internal'
  /** The 10px nub pointing at the anchor — the margin popover's, under its chip (§6 row 347). */
  nub?: boolean
  /** Opened over a modal — see `Popover`'s `layer`. */
  layer?: number
  children: ReactNode
}) {
  const phone = usePhone()
  if (!open) return null

  if (phone) {
    return (
      /*
       * Every phone board draws the same sheet (cb-17 · 2, cb-22, qb-09 · 2,
       * qb-10 · 5): the page under a 22% ink scrim, an 18px top corner, a
       * 36 × 4 grabber, the name over one line of facts with the 34px tinted
       * ✕, and a footer that is one row of 48px buttons — `EditorActions`
       * draws those on a phone, so every caller gets them.
       */
      <div className={cn('fixed inset-0 z-50 flex flex-col bg-[rgba(29,29,26,0.22)]',
        fullScreen ? 'justify-start' : 'justify-end')}
        onClick={onClose}>
        <div
          role="dialog"
          aria-label={label}
          data-editor-shell=""
          data-sheet=""
          onClick={(event) => event.stopPropagation()}
          className={cn('flex flex-col overflow-hidden border-line bg-surface pb-safe',
            fullScreen
              ? 'h-app'
              : 'max-h-editor rounded-t-sheet border-t shadow-[0_-12px_32px_rgba(20,20,10,0.16)]')}
        >
          {!fullScreen && (
            <span aria-hidden className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-chip bg-line-strong" />
          )}
          <header className={cn('flex shrink-0 items-center border-b border-line-soft',
            fullScreen ? 'gap-3 px-4 py-3.5' : 'min-h-[56px] gap-2 pb-2.5 pl-4 pr-3.5 pt-1.5')}>
            {header ?? <b className="min-w-0 flex-1 truncate text-[16px] font-bold text-text">{label}</b>}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="ml-auto flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-chip bg-hover text-text-muted"
            >
              <IconClose size={17} />
            </button>
          </header>
          <div className={cn('min-h-0 flex-1 px-4',
            tone === 'internal' ? 'pb-3 pt-3' : 'pb-3 pt-3.5',
            fill ? 'flex flex-col' : 'overflow-y-auto')}>
            {children}
          </div>
          {footer && (
            /* 10 above the buttons and a rule, then the home indicator's own room (`pb-safe`). */
            <footer data-sheet-footer=""
              className="flex shrink-0 items-center gap-2 border-t border-line-soft bg-surface px-4 pb-1.5 pt-2.5">
              {footer}
            </footer>
          )}
        </div>
      </div>
    )
  }

  /*
   * No anchor: opened from a list row rather than from a control on the page
   * — the catalog's service pop-out (cb-04). The same box, centred over the
   * page on a scrim, because a popover with nothing to hang off has nowhere
   * to be placed.
   */
  if (anchor === null) {
    return <CentredShell {...{ onClose, label, width, header, footer, fill, children }} />
  }

  const head = (
    /*
     * Board 7: 14 × 16 around a 36px tile, 12 between the tile and the
     * name. Every builder popover is 480 wide (`width` above) and wears
     * this header — the board's one anatomy.
     */
    <header className={cn('flex shrink-0 items-center',
      tone === 'internal' ? 'gap-2.5 pb-0 pl-4 pr-3.5 pt-3'
        : cn('border-b border-line-soft', header ? 'gap-3 px-4 py-3.5' : 'gap-2 px-4 py-3'))}>
      {header ?? <b className="min-w-0 truncate text-[13.5px] font-bold text-text">{label}</b>}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-field text-text-faint hover:text-text"
      >
        <IconClose size={16} />
      </button>
    </header>
  )
  /*
   * Header and footer hold; the fields between them scroll. The box caps
   * itself to whatever room the window has, and Done living in a footer that
   * cannot be scrolled away is the point of the cap.
   */
  const body = (
    <div className={cn('min-h-0 flex-1 px-4',
      tone === 'internal' ? 'pb-3.5 pt-3' : 'pb-4 pt-3.5',
      fill ? 'flex flex-col' : 'overflow-y-auto')}>
      {children}
    </div>
  )
  const foot = footer && (
    <footer className={cn('flex shrink-0 items-center gap-2 border-t border-line-soft',
      tone === 'internal' ? 'bg-surface-alt px-4 py-2.5 text-[12px]' : 'bg-surface px-4 py-3')}>
      {footer}
    </footer>
  )

  /*
   * Two of the boxes hang off what was pressed, as a menu does, and keep
   * `Popover`'s placement:
   *
   * - the margin popover (qb-04, qb-08 · B) hangs *under its chip*, its right
   *   edge on the chip's right edge and its nub on the chip's centre — the
   *   board puts the 392px box at `right: 44` of a card whose chip ends 44 in,
   *   and the nub 55 from the box's right edge, which is the chip's middle.
   *   No scrim: the page stays as it was (both boards).
   * - a search (`fill`) hangs under the link that asked for it (cb-04 · 3,
   *   "anchored under + Materials").
   *
   * Every other editor is a form, and those are placed against the page.
   */
  if (tone === 'internal' || fill) {
    return (
      <Popover
        open
        onClose={onClose}
        anchor={anchor}
        label={label}
        width={width}
        maxHeight={maxHeight}
        align={tone === 'internal' ? 'right' : 'left'}
        marker="editor-shell"
        layer={layer}
        className={tone === 'internal' ? '!bg-surface-alt' : undefined}
      >
        {nub && <Nub anchor={anchor} internal={tone === 'internal'} />}
        {head}
        {body}
        {foot}
      </Popover>
    )
  }

  return (
    <EditorPanel anchor={anchor} onClose={onClose} label={label} width={width}
      maxHeight={maxHeight} layer={layer}>
      {head}
      {body}
      {foot}
    </EditorPanel>
  )
}

/** The window's edges are not a surface — `placeFloating`'s MARGIN. */
const EDGE = 12
/** Between a control and a box hung under it — `placeFloating`'s GAP. */
const UNDER = 6

interface Box { left: number; right: number; top: number; bottom: number }

/** Between the end of a name and a box opened beside it (step 24 item 2). */
const BESIDE = 12
/**
 * How far above a name's middle the box's top goes, so the box's own first
 * line — the 36px tile and the name in its header — sits level with the name
 * that was clicked rather than hanging below it.
 */
const LEVEL = 22
/** A side with less room than this is not worth capping the box to. */
const MIN_SIDE = 240

export interface EditorPlacement {
  left: number
  top: number
  maxHeight: number
  /** Where the scrim starts: the nav rail is not dimmed (cb-15 · C, `left: 60px`). */
  scrimLeft: number
  /** Which way it went — for the tests, and for whoever reads a screenshot. */
  side: 'right' | 'left' | 'under' | 'over' | 'slid'
}

/**
 * Where an editor goes: **beside what was clicked, close to it, never on it.**
 *
 * Three defects shaped this. O1: the box was placed like a menu and a 700px
 * editor opened from a row half-way down the page sat over the app's own
 * header. The fix right-aligned every editor to the visit card, a card's
 * width from the click (step 22 item 1: "beside where you clicked like
 * before"), so step 22 hung it *under* the anchor and slid it up until it was
 * whole — which, from a name low on the screen, slid it straight back over
 * the name (Aaron, step 24 item 2: "the popup appears on top of the item you
 * clicked on … it should appear to the right of what you clicked and where
 * your pointer is. Very close to the end of the name so as not to be far
 * away, but not on top of either").
 *
 * - **The band** is the scroll container's top (the frame's `<main>`, whose
 *   top is the header's bottom) to the window's bottom, 12 in from each. It
 *   is read from the container rather than assumed, because the page scrolls
 *   *inside* `<main>` under a header that does not move.
 * - **Right of the name**: `reach` is where the name's *words* end — a
 *   service name is a 7rem button however short "Mulch" is — and the press
 *   may have landed past them, so the box starts 12px past whichever is
 *   further right. Its first line sits level with the name, then it slides
 *   up or down to be whole; being beside the name, sliding never covers it.
 * - **Left of it** when the right has no room and the left has.
 * - **Under, then over**, left edges together, only when neither side has
 *   room — and only where the whole box fits, so it still covers nothing.
 * - **Capped to the roomier side** when the box is taller than either, as
 *   long as that side has `MIN_SIDE` to give; past that the window really is
 *   too short, and the box takes the whole band and slides, as step 22 did.
 *
 * Pure, so the arithmetic is pinned by a test rather than by a screenshot.
 */
export function placeEditor({ anchor, frame, viewport, width, wanted, reach, pointer }: {
  anchor: Box
  /** The scroll container's top-left, in viewport coordinates — 0,0 with none. */
  frame: { top: number; left: number }
  viewport: { width: number; height: number }
  width: number
  /** How tall the box would be with nothing in its way — 0 before it is measured. */
  wanted: number
  /** Where the anchor's words end, when they end before its box does. */
  reach?: number
  /** Where the press that opened it landed, when it landed on the anchor. */
  pointer?: { x: number; y: number } | null
}): EditorPlacement {
  const bandTop = Math.max(0, frame.top) + EDGE
  const bandBottom = viewport.height - EDGE
  const room = Math.max(0, bandBottom - bandTop)
  const height = wanted > 0 ? Math.min(wanted, room) : room
  const scrimLeft = Math.max(0, frame.left)

  const minLeft = Math.max(0, frame.left) + EDGE
  const maxLeft = viewport.width - EDGE - width
  const within = (top: number) => Math.max(bandTop, Math.min(top, bandBottom - height))
  const level = within((anchor.top + anchor.bottom) / 2 - LEVEL)

  const end = Math.max(Math.min(reach ?? anchor.right, anchor.right), pointer?.x ?? -Infinity)
  const right = end + BESIDE
  if (right <= maxLeft) return { left: right, top: level, maxHeight: room, scrimLeft, side: 'right' }

  const start = Math.min(anchor.left, pointer?.x ?? Infinity)
  const left = start - BESIDE - width
  if (left >= minLeft) return { left, top: level, maxHeight: room, scrimLeft, side: 'left' }

  /* Never under the nav rail, never past the window's right edge — the right edge wins a tie. */
  const across = Math.max(EDGE, Math.min(Math.max(anchor.left, minLeft), maxLeft))
  const below = anchor.bottom + UNDER
  const above = anchor.top - UNDER
  if (below + height <= bandBottom) {
    return { left: across, top: below, maxHeight: room, scrimLeft, side: 'under' }
  }
  if (above - height >= bandTop) {
    return { left: across, top: above - height, maxHeight: room, scrimLeft, side: 'over' }
  }

  const roomBelow = bandBottom - below
  const roomAbove = above - bandTop
  if (Math.max(roomBelow, roomAbove) >= MIN_SIDE) {
    return roomBelow >= roomAbove
      ? { left: across, top: below, maxHeight: roomBelow, scrimLeft, side: 'under' }
      : { left: across, top: bandTop, maxHeight: roomAbove, scrimLeft, side: 'over' }
  }
  return { left: across, top: within(below), maxHeight: room, scrimLeft, side: 'slid' }
}

/**
 * Where a name's words end, in viewport coordinates.
 *
 * A Range over the anchor's contents measures the text rather than the box,
 * and is held to the box because a truncated name's text runs on past its
 * ellipsis. Null where there is nothing to measure — no text, or a browser
 * (and jsdom) that cannot measure a Range.
 */
function wordsEnd(anchor: HTMLElement): number | null {
  if (typeof document.createRange !== 'function') return null
  const range = document.createRange()
  range.selectNodeContents(anchor)
  if (typeof range.getBoundingClientRect !== 'function') return null
  const box = range.getBoundingClientRect()
  return box.width > 0 ? box.right : null
}

/*
 * The last press, so a box can open beside the pointer as well as beside the
 * name (step 24 item 2). Read once, when the box is placed, and only when the
 * press landed on the anchor and was recent: an editor opened from the
 * keyboard, or by the builder after a save, has no pointer to be beside.
 */
let lastPress: { x: number; y: number; target: EventTarget | null; at: number } | null = null
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', (event) => {
    lastPress = { x: event.clientX, y: event.clientY, target: event.target, at: Date.now() }
  }, true)
}
function pressOn(anchor: HTMLElement): { x: number; y: number } | null {
  if (!lastPress || Date.now() - lastPress.at > 2000) return null
  if (!(lastPress.target instanceof Node) || !anchor.contains(lastPress.target)) return null
  return { x: lastPress.x, y: lastPress.y }
}

/**
 * The form editor's box: placed by `placeEditor`, on a scrim.
 *
 * The measuring is `Popover`'s, kept to its two rules (read `overlays.tsx`
 * for why): the wanted height is read **with the cap off**, and only when the
 * contents change — a scroll or a resize only *re-places*, which reads and
 * never writes, so a list scrolled inside the box cannot flicker back to the
 * top.
 *
 * The scrim is cb-15 · C's: `rgba(29,29,26,.10)` from the nav rail's edge to
 * the window's, header included. A press on it is an outside press, so it
 * closes the box as one always did — it carries no overlay layer, so
 * `useDismiss` reads it as outside. Opened over a modal (`layer`), there is
 * no second scrim: the modal already dims the page.
 */
function EditorPanel({ anchor, onClose, label, width, maxHeight, layer, children }: {
  anchor: HTMLElement
  onClose: () => void
  label: string
  width: number
  maxHeight?: number
  layer?: number
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(true, onClose, ref, layer ?? 0)
  const wanted = useRef(0)
  const [at, setAt] = useState<EditorPlacement | null>(null)
  /*
   * The press is read once, when the box opens: the pointer moves on to the
   * box itself, and a box that followed it on every re-place would walk away
   * from the name it is about. Kept relative to the anchor so a scroll moves
   * the two together.
   */
  const press = useRef<{ dx: number } | null | undefined>(undefined)
  if (press.current === undefined) {
    const landed = pressOn(anchor)
    press.current = landed ? { dx: landed.x - anchor.getBoundingClientRect().left } : null
  }

  const apply = useCallback(() => {
    if (!ref.current) return
    const frame = anchor.closest('main')?.getBoundingClientRect()
    const box = anchor.getBoundingClientRect()
    const found = placeEditor({
      anchor: box,
      frame: frame ? { top: frame.top, left: frame.left } : { top: 0, left: 0 },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      width,
      wanted: Math.min(wanted.current, maxHeight ?? Number.POSITIVE_INFINITY),
      reach: wordsEnd(anchor) ?? undefined,
      pointer: press.current ? { x: box.left + press.current.dx, y: box.top } : null,
    })
    if (maxHeight !== undefined) found.maxHeight = Math.min(found.maxHeight, maxHeight)
    setAt((current) => (current
      && current.left === found.left && current.top === found.top
      && current.maxHeight === found.maxHeight && current.scrimLeft === found.scrimLeft
      && current.side === found.side
      ? current
      : found))
  }, [anchor, width, maxHeight])

  const measure = useCallback(() => {
    const node = ref.current
    if (!node) return
    const scrolled = Array.from(node.querySelectorAll('*'))
      .filter((child): child is HTMLElement => child instanceof HTMLElement && child.scrollTop > 0)
      .map((child) => [child, child.scrollTop] as const)
    const held = node.style.maxHeight
    node.style.maxHeight = 'none'
    wanted.current = node.getBoundingClientRect().height
    node.style.maxHeight = held
    for (const [child, top] of scrolled) child.scrollTop = top
    apply()
  }, [apply])

  useLayoutEffect(() => {
    measure()
    /* The page scrolling under the box moves its anchor; the box's own list scrolling does not. */
    const onScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Node && ref.current?.contains(target)) return
      apply()
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', apply)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', apply)
    }
  }, [measure, apply, children])

  return (
    <>
      {layer === undefined && (
        <div
          aria-hidden
          data-editor-scrim=""
          style={{ left: at?.scrimLeft ?? 0 }}
          className="fixed bottom-0 right-0 top-0 z-[49] bg-[rgba(29,29,26,0.10)]"
        />
      )}
      <div
        ref={ref}
        role="dialog"
        aria-label={label}
        data-editor-shell=""
        data-side={at?.side}
        {...(layer !== undefined ? { 'data-overlay-layer': layer } : {})}
        style={{ width, left: at?.left ?? 0, top: at?.top ?? 0, maxHeight: at?.maxHeight }}
        className={cn(
          'fixed z-50 flex max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-popup border border-line',
          'bg-surface shadow-popover',
          /* Transparent, not `invisible`, until placed — `autoFocus` must still land (see `Popover`). */
          at ? 'visible' : 'pointer-events-none opacity-0',
        )}
      >
        {children}
      </div>
    </>
  )
}

function CentredShell({ onClose, label, width, header, footer, fill, children }: {
  onClose: () => void
  label: string
  width: number
  header?: ReactNode
  footer?: ReactNode
  fill?: boolean
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(true, onClose, ref, 50)
  return createPortal((
    <div data-overlay-layer={50} className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-editor-shell=""
        style={{ width }}
        className="flex max-h-sheet max-w-full flex-col overflow-hidden rounded-popup border border-line bg-surface shadow-popover"
      >
        <header className={cn('flex shrink-0 items-center border-b border-line-soft',
          header ? 'gap-3 px-4 py-3.5' : 'gap-2 px-4 py-3')}>
          {header ?? <b className="min-w-0 truncate text-[13.5px] font-bold text-text">{label}</b>}
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-field text-text-faint hover:text-text"
          >
            <IconClose size={16} />
          </button>
        </header>
        <div className={cn('min-h-0 flex-1 px-4 pb-4 pt-3.5', fill ? 'flex flex-col' : 'overflow-y-auto')}>
          {children}
        </div>
        {footer && (
          <footer className="flex shrink-0 items-center gap-2 border-t border-line-soft bg-surface px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  ), document.body)
}

/**
 * The nub: a 10px square turned 45°, on the popover's edge facing the anchor.
 *
 * `position: fixed` so the popover's `overflow: hidden` cannot clip it — a
 * fixed box escapes its ancestors' clipping, which is the only reason it can
 * live inside the panel at all. It follows the panel wherever `placeFloating`
 * put it, above the anchor or below, and draws nothing when the two have
 * drifted apart sideways.
 */
function Nub({ anchor, internal }: { anchor: HTMLElement; internal: boolean }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [at, setAt] = useState<{ left: number; top: number; below: boolean } | null>(null)

  useLayoutEffect(() => {
    let frame = 0
    const place = () => {
      const panel = ref.current?.closest<HTMLElement>('[role="dialog"]')
      if (!panel) return
      const a = anchor.getBoundingClientRect()
      const p = panel.getBoundingClientRect()
      const centre = a.left + a.width / 2
      if (centre < p.left + 16 || centre > p.right - 16) { setAt(null); return }
      const below = p.top >= a.bottom - 1
      const next = { left: centre - 5, top: below ? p.top - 5 : p.bottom - 5, below }
      setAt((current) => (current && current.left === next.left && current.top === next.top
        && current.below === next.below ? current : next))
    }
    const later = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(place) }
    later()
    window.addEventListener('scroll', later, true)
    window.addEventListener('resize', later)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', later, true)
      window.removeEventListener('resize', later)
    }
  }, [anchor])

  return (
    <span
      ref={ref}
      aria-hidden
      data-popover-nub=""
      style={at ? { left: at.left, top: at.top } : { visibility: 'hidden' }}
      className={cn('pointer-events-none fixed z-[51] h-2.5 w-2.5 rotate-45 border-line',
        internal ? 'bg-surface-alt' : 'bg-surface',
        at?.below === false ? 'border-b border-r' : 'border-l border-t')}
    />
  )
}

/**
 * The header every editor wears: tile, name, and one line of facts.
 *
 * The facts are what tell you this is the row you meant to open — "Material ·
 * Hardscape · $3.10 / sq ft cost", "Weekly · Apr 7 – Oct 27 · 30 visits". A
 * header that said only "Visit" made you close the box to find out which one
 * you had opened.
 */
export function EditorHeader({ kind, name, facts, icon }: {
  kind: TileKind
  name: string
  facts?: ReactNode
  /** A glyph other than the kind's own — the proposal popover's eye. */
  icon?: ComponentType<{ size?: number; strokeWidth?: number }>
}) {
  /*
   * A sheet's header is words only (cb-17 · 2, cb-22 · 3, qb-10 · 5): the
   * name at 16/700 over the facts at 12, no tile — the sheet already fills
   * the screen with the one thing it is about.
   */
  if (usePhone()) {
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        <b className="truncate text-[16px] font-bold text-text">{name}</b>
        {facts && <span className="truncate text-[12px] text-text-muted">{facts}</span>}
      </div>
    )
  }
  return (
    <>
      <KindTile kind={kind} size={36} icon={icon} outlined />
      <div className="flex min-w-0 flex-col gap-px">
        <b className="truncate text-[15px] font-bold text-text">{name}</b>
        {facts && <span className="truncate text-[12px] text-text-muted">{facts}</span>}
      </div>
    </>
  )
}

/**
 * The footer every editor wears: a fact on the left, the actions on the right.
 *
 * `fact` is the number this box is about, so the thing you came to change and
 * the thing it changes are visible at once. `onRemove` is the reason the
 * footer has two sides at all — the box you opened to look at something is
 * where you decide you do not want it, which is why the mockups put Remove
 * beside Done rather than hiding it on a ⋯ menu.
 *
 * **On a phone it is a row of 48px buttons and nothing else** (O6): one
 * full-width Done (cb-17 · 2), or a pair whose second is twice the first —
 * `Remove · Done` (qb-10 · 5, cb-22 · 3–4), `Cancel · Done` (cb-22 · 5–6). No
 * board draws the fact in a sheet's footer: a sheet is the whole screen and
 * the number is already on it.
 */
export function EditorActions({
  fact, onRemove, removeLabel = REMOVE, onDone, doneLabel = DONE, busy, disabled, extra, onCancel,
}: {
  fact?: ReactNode
  onRemove?: () => void
  removeLabel?: string
  onDone: () => void
  doneLabel?: string
  busy?: boolean
  /** Done is refused — a price below standard with no reason against it. */
  disabled?: boolean
  /** Anything that belongs before Remove: "Reset to standard". */
  extra?: ReactNode
  /**
   * A phone's `Cancel`, left of Done where there is nothing to remove — the
   * measure sheet's (cb-22 · 5–6). A desktop box closes on its ✕ and Esc, and
   * no desktop board draws a Cancel, so there it is not drawn.
   */
  onCancel?: () => void
}) {
  const phone = usePhone()
  if (phone) {
    const second = onRemove ?? onCancel
    return (
      <span data-sheet-actions="" className="flex w-full items-center gap-2">
        {extra && (
          /* Not drawn on a phone board; when a box has one it takes the pair's shape. */
          <span className="flex min-w-0 flex-1 [&>*]:!h-12 [&>*]:w-full [&>*]:!rounded-record [&>*]:!text-[14px]">
            {extra}
          </span>
        )}
        {onRemove
          ? (
            <Button variant="danger" className={cn(SHEET_BUTTON, 'min-w-0 flex-1 !text-[14px]')} onClick={onRemove}>
              {removeLabel}
            </Button>
          )
          : onCancel && (
            <Button className={cn(SHEET_BUTTON, 'min-w-0 flex-1 !text-[14px]')} onClick={onCancel}>
              {CANCEL}
            </Button>
          )}
        <Button variant="primary"
          className={cn(SHEET_BUTTON, '!text-[15px]', second || extra ? 'min-w-0 flex-[2_1_0%]' : 'w-full')}
          disabled={disabled || busy} onClick={onDone}>
          {busy ? 'Saving…' : doneLabel}
        </Button>
      </span>
    )
  }
  return (
    <>
      {fact && <span className="min-w-0 truncate text-[12.5px] text-text-muted">{fact}</span>}
      <span className="ml-auto flex shrink-0 items-center gap-2">
        {extra}
        {onRemove && (
          <Button variant="danger" className={FOOTER_BUTTON} onClick={onRemove}>
            {removeLabel}
          </Button>
        )}
        <Button variant="primary" className={FOOTER_BUTTON} disabled={disabled || busy} onClick={onDone}>
          {busy ? 'Saving…' : doneLabel}
        </Button>
      </span>
    </>
  )
}

/**
 * A phone sheet's footer button (cb-17 · 2, cb-18, cb-22, qb-10 · 5): 48 tall,
 * radius 14, 600. Important, because `Button`'s own 32px size class is in the
 * same string and a plain `h-12` beside it is decided by stylesheet order.
 */
export const SHEET_BUTTON = '!h-12 !rounded-record !px-3 font-semibold'

/** Board 7's footer buttons: 34 tall, 14 across, 12.5/700. */
export const FOOTER_BUTTON = 'h-[34px] px-3.5 text-[12.5px] font-bold rounded-[9px]'

/**
 * The fact a footer states — `Visit total $121.33` — with the amount in ink
 * and the words muted, as Board 7 draws every one of them.
 */
export function FooterTotal({ label, amount }: { label: string; amount: ReactNode }) {
  return <>{label} <b className="font-bold text-text tnum">{amount}</b></>
}

/**
 * The one input an editor field holds, so thirty of them cannot drift apart.
 *
 * It is `controlClass()` — the *same* 36px box the record forms wear, not a
 * 32px one of its own. It used to be its own string, and the result was a
 * quote builder where a text box beside a dropdown was four pixels shorter
 * and two of a different radius: the two controls did not look like they
 * belonged to each other, because they did not.
 *
 * `tone="number"` right-aligns; `tone="locked"` is a value the box is showing
 * rather than asking for — a visit count the schedule decides.
 */
export function EditorInput({
  tone = 'text', big, className, ...props
}: JSX.IntrinsicElements['input'] & {
  tone?: 'text' | 'number' | 'locked'
  /**
   * The one big field a popover has — the measure on a service, the quantity
   * on a line: 44 tall, 17/700 (Board 7). Everything else is 36.
   */
  big?: boolean
}) {
  return (
    <input
      {...props}
      data-editor-input=""
      data-size={big ? 'big' : undefined}
      readOnly={tone === 'locked' || props.readOnly}
      className={cn(
        controlClass({ readOnly: tone === 'locked', size: big ? 'big' : 'default' }),
        big && 'font-bold',
        tone !== 'text' && 'text-right',
        tone === 'locked' && 'cursor-default text-text-muted',
        className)}
    />
  )
}

/** The same box, taller, for the three notes a visit carries. */
export function EditorTextArea({
  className, ...props
}: JSX.IntrinsicElements['textarea']) {
  return (
    <textarea
      {...props}
      className={cn(controlClass(), 'h-auto resize-y py-[7px] leading-[1.45]', className)}
    />
  )
}

/**
 * The editor's own menu, at the field's height.
 *
 * `MenuSelect` draws the panel; this gives its trigger the same box as
 * `EditorInput`, which is the whole point of the exercise.
 */
export function EditorSelect({
  label, value, onChange, options, placeholder, clearable, clearLabel, disabled, className,
}: {
  label: string
  value: string | null
  onChange: (value: string | null) => void
  options: MenuOption[]
  placeholder?: string
  clearable?: boolean
  clearLabel?: string
  disabled?: boolean
  className?: string
}) {
  return (
    <MenuSelect
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      clearable={clearable}
      clearLabel={clearLabel}
      disabled={disabled}
      className={cn(controlClass({ disabled }), 'justify-start', className)}
      triggerContent={(chosen) => (
        <>
          <span className={cn('min-w-0 flex-1 truncate text-left',
            !chosen && 'font-medium text-text-faint')}>
            {chosen?.label ?? placeholder ?? 'Choose…'}
          </span>
          <span aria-hidden className="ml-auto shrink-0 pl-2 text-text-muted">
            <IconChevronDown size={12} />
          </span>
        </>
      )}
    />
  )
}
